import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, CalendarDots, Clock } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const PHASE_COLORS = { 'menstruação': 'bg-red-100 text-red-600', 'folicular': 'bg-blue-50 text-blue-600', 'ovulação': 'bg-green-50 text-green-600', 'lútea': 'bg-amber-50 text-amber-600', 'antes do ciclo': 'bg-brand-bg text-brand-muted' };

export default function CicloPage() {
  const confirmDelete = useConfirmDelete();
  const [cycles, setCycles] = useState([]);
  const [prediction, setPrediction] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ start_date: new Date().toISOString().split('T')[0], duration: '5', cycle_length: '28' });

  const fetch = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        axios.get(`${API}/api/my-cycles`),
        axios.get(`${API}/api/my-cycles/prediction`),
      ]);
      setCycles(cRes.data || []);
      setPrediction(pRes.data);
    } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    try {
      await axios.post(`${API}/api/my-cycles`, { start_date: form.start_date, duration: parseInt(form.duration) || 5, cycle_length: parseInt(form.cycle_length) || 28 });
      toast.success('Ciclo registado'); setShowAdd(false); fetch();
    } catch { toast.error('Erro'); }
  };
  const del = async (id) => {
    const c = cycles.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o ciclo com início em ${c?.start_date || ''}.` });
    if (!ok) return;
    await axios.delete(`${API}/api/my-cycles/${id}`); fetch();
  };

  return (
    <div className="fade-in" data-testid="ciclo-page">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-medium text-xl text-brand-text">Ciclo menstrual</h2>
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-cycle-btn"><Plus size={16} /> Registar</button>
      </div>

      {/* Current phase */}
      {prediction?.current_phase && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="cycle-phase">
          <p className="text-xs text-brand-muted mb-1">Fase actual</p>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${PHASE_COLORS[prediction.current_phase] || 'bg-brand-bg text-brand-muted'}`}>{prediction.current_phase}</span>
            {prediction.day_of_cycle >= 0 && <span className="text-xs text-brand-muted">Dia {prediction.day_of_cycle} do ciclo</span>}
          </div>
        </div>
      )}

      {/* Prediction */}
      {prediction?.next_start && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="cycle-prediction">
          <p className="text-xs text-brand-muted mb-2">Previsão</p>
          <div className="grid grid-cols-2 gap-3">
            <div><p className="text-sm font-medium text-brand-text">{prediction.next_start}</p><p className="text-[10px] text-brand-muted">Próximo início</p></div>
            <div><p className="text-sm font-medium text-brand-text">{prediction.avg_cycle_length} dias</p><p className="text-[10px] text-brand-muted">Ciclo médio</p></div>
          </div>
        </div>
      )}

      {/* History */}
      <h3 className="font-heading font-medium text-sm text-brand-text mb-2">Histórico</h3>
      {cycles.length === 0 ? (
        <p className="text-center text-brand-muted py-8 text-sm">Sem registos. Adiciona o teu primeiro ciclo.</p>
      ) : (
        <div className="space-y-2">
          {cycles.map(c => (
            <div key={c.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between group" data-testid={`cycle-${c.id}`}>
              <div>
                <p className="text-sm font-medium text-brand-text">{fmtDatePt(c.start_date)}</p>
                <p className="text-[10px] text-brand-muted">{c.duration} dias de menstruação, ciclo de {c.cycle_length} dias</p>
              </div>
              <button onClick={() => del(c.id)} className="hidden group-hover:block text-brand-placeholder hover:text-red-500" data-testid={`delete-cycle-${c.id}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar ciclo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data de início</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Duração da menstruação (dias)</Label><Input type="number" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} className="rounded-xl border-brand-border" min="1" max="15" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Duração do ciclo (dias)</Label><Input type="number" value={form.cycle_length} onChange={e => setForm({ ...form, cycle_length: e.target.value })} className="rounded-xl border-brand-border" min="20" max="45" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
